import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { eprelBase64ToImageUri } from 'components/Pdf/EprelPicture';
import { getPictures } from 'domains/pictures/Pictures.store';
import { ERROR, getData, hasData, NOT_FOUND } from 'utils';

export interface EprelPdfImages {
  images: Map<string, string>;
  isReady: boolean;
}

export const useEprelPdfImages = (urls: string[]): EprelPdfImages => {
  const pictures = useSelector((state: RootState) => getPictures(state, urls));
  const startedRef = useRef<Set<string>>(new Set());
  const [images, setImages] = useState<Map<string, string>>(new Map());
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;

    function handleConverted(url: string, uri: string | undefined): void {
      if (!isMounted) {
        return;
      }
      setResolved(function addResolvedUrl(prev: Set<string>): Set<string> {
        return prev.has(url) ? prev : new Set(prev).add(url);
      });
      if (uri) {
        setImages(function addUrlImage(prev: Map<string, string>): Map<string, string> {
          return new Map(prev).set(url, uri);
        });
      }
    }

    function startConversion(url: string): void {
      const base64 = getData(pictures.get(url));
      if (!base64 || startedRef.current.has(url)) {
        return;
      }
      startedRef.current.add(url);
      eprelBase64ToImageUri(base64).then(function onConverted(uri) {
        handleConverted(url, uri);
      });
    }

    urls.forEach(startConversion);

    return function cleanup(): void {
      isMounted = false;
    };
  }, [urls, pictures]);

  function isUrlReady(url: string): boolean {
    const value = pictures.get(url);
    if (hasData(value)) {
      return resolved.has(url);
    }

    return value === ERROR || value === NOT_FOUND;
  }

  const isReady = urls.every(isUrlReady);

  return { images, isReady };
};
